import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * ContentProtection — site-wide deterrents against copying, screenshots and
 * screen recording.
 *
 * IMPORTANT (honest limitation): a web page CANNOT truly stop an OS-level
 * screenshot or screen recorder. These are strong *deterrents*:
 *  - copy / cut / right-click / text-selection / image-drag disabled everywhere
 *    (except inside form fields so login / OTP / forms keep working)
 *  - PrintScreen wipes the clipboard and flashes a black warning screen
 *  - DevTools / view-source shortcuts blocked
 *  - a tiled watermark sits over the whole site so any capture is traceable
 *  - the screen goes black whenever the window loses focus or is hidden
 *    (deters recorders / alt-tab captures) and while DevTools is open
 */

// Global CSS: disable selection/callout site-wide, keep form fields usable.
const GLOBAL_CSS = `
  html, body {
    -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none;
    -webkit-touch-callout: none;
  }
  input, textarea, select, [contenteditable="true"], .allow-select, .allow-select * {
    -webkit-user-select: text !important; -moz-user-select: text !important; -ms-user-select: text !important; user-select: text !important;
    -webkit-touch-callout: default !important;
  }
  img, video, canvas { -webkit-user-drag: none; user-drag: none; }
  ::selection { background: transparent; }
`;

function inField(target) {
    return !!(target && target.closest && target.closest('input, textarea, select, [contenteditable="true"], .allow-select'));
}

export default function ContentProtection() {
    const [blackout, setBlackout] = useState(false);
    const [reason, setReason] = useState("");
    const [enabled, setEnabled] = useState(true); // protect by default until config loads
    const flashTimer = useRef(null);

    // Admin can turn screenshot / screen-recording protection ON/OFF site-wide
    // (Admin → Settings → Content Protection). Re-checked on mount.
    useEffect(() => {
        let alive = true;
        api.get("/branding")
            .then(({ data }) => { if (alive) setEnabled(data?.content_protection !== false); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!enabled) return; // protection disabled by admin — attach nothing
        // inject global css
        const style = document.createElement("style");
        style.setAttribute("data-content-protection", "1");
        style.appendChild(document.createTextNode(GLOBAL_CSS));
        document.head.appendChild(style);

        const stop = (e) => { e.preventDefault(); e.stopPropagation(); return false; };

        const onContextMenu = (e) => { if (!inField(e.target)) stop(e); };
        const onCopy = (e) => {
            if (inField(e.target)) return; // allow copy of own typed input
            stop(e);
            toast.error("Copying is disabled on this site.");
        };
        const onCut = (e) => { if (!inField(e.target)) stop(e); };
        const onDragStart = (e) => { if (e.target && (e.target.tagName === "IMG" || !inField(e.target))) stop(e); };
        const onSelectStart = (e) => { if (!inField(e.target)) stop(e); };

        const flashBlack = (msg) => {
            setReason(msg);
            setBlackout(true);
            clearTimeout(flashTimer.current);
            flashTimer.current = setTimeout(() => setBlackout(false), 1400);
        };

        const clearClipboard = () => {
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(" ").catch(() => {});
                }
            } catch { /* ignore */ }
        };

        const onKeyDown = (e) => {
            const k = e.key;
            // PrintScreen
            if (k === "PrintScreen") {
                clearClipboard();
                flashBlack("Screenshots are not allowed");
                toast.error("Screenshots are not allowed on this site.");
                e.preventDefault();
                return;
            }
            // DevTools / view-source / save / print
            const ctrl = e.ctrlKey || e.metaKey;
            if (k === "F12"
                || (ctrl && e.shiftKey && ["I", "J", "C", "i", "j", "c"].includes(k))
                || (ctrl && ["u", "U", "s", "S", "p", "P"].includes(k))) {
                e.preventDefault();
                e.stopPropagation();
                toast.error("This action is disabled on this site.");
            }
        };
        // Some browsers only surface PrintScreen on keyup
        const onKeyUp = (e) => {
            if (e.key === "PrintScreen") { clearClipboard(); flashBlack("Screenshots are not allowed"); }
        };

        // Tab hidden (switched away / minimised) → black screen. We deliberately
        // do NOT black out on plain window blur — that fires for in-page focus
        // changes (dialogs, autofill, dev consoles) and would wreck normal use.
        const onVisibility = () => {
            if (document.visibilityState === "hidden") { setReason("Screen protected"); setBlackout(true); }
            else setBlackout(false);
        };

        // DevTools shortcuts are already blocked via keydown. We intentionally
        // do NOT auto-blackout on a window-size heuristic — it false-positives
        // on docked toolbars / small windows and would black the whole app.

        document.addEventListener("contextmenu", onContextMenu, true);
        document.addEventListener("copy", onCopy, true);
        document.addEventListener("cut", onCut, true);
        document.addEventListener("dragstart", onDragStart, true);
        document.addEventListener("selectstart", onSelectStart, true);
        document.addEventListener("keydown", onKeyDown, true);
        document.addEventListener("keyup", onKeyUp, true);
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            document.head.removeChild(style);
            document.removeEventListener("contextmenu", onContextMenu, true);
            document.removeEventListener("copy", onCopy, true);
            document.removeEventListener("cut", onCut, true);
            document.removeEventListener("dragstart", onDragStart, true);
            document.removeEventListener("selectstart", onSelectStart, true);
            document.removeEventListener("keydown", onKeyDown, true);
            document.removeEventListener("keyup", onKeyUp, true);
            document.removeEventListener("visibilitychange", onVisibility);
            clearTimeout(flashTimer.current);
        };
    }, [enabled]);

    return (
        <>
            {/* Black protective overlay */}
            {blackout && (
                <div
                    data-testid="content-blackout"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 2147483600,
                        background: "#000",
                        color: "rgba(255,255,255,0.85)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "12px",
                        textAlign: "center",
                        padding: "24px",
                        fontFamily: "Georgia, serif",
                    }}
                >
                    <div style={{ fontSize: "42px", lineHeight: 1 }}>🔒</div>
                    <div style={{ fontSize: "18px", fontWeight: 700 }}>{reason || "Screen protected"}</div>
                    <div style={{ fontSize: "13px", opacity: 0.7, maxWidth: 340 }}>
                        This content is protected. Screenshots, screen recording and copying are not permitted.
                    </div>
                </div>
            )}
        </>
    );
}
